//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
const adb2cDomainName = process.env.REACT_APP_ADB2C_DOMAIN_NAME;
const adb2cClientID = process.env.REACT_APP_ADB2C_CLIENT_ID;
const adb2cSignUpSignIn = process.env.REACT_APP_ADB2C_SIGNUP_SIGNIN;
const adb2cSignUp = process.env.REACT_APP_ADB2C_SIGNUP;
const adb2cPasswordReset = process.env.REACT_APP_ADB2C_PASSWORD_RESET;
const adb2cPasswordChange = process.env.REACT_APP_ADB2C_PASSWORD_CHANGE;
const adb2cRedirectUri = process.env.REACT_APP_ADB2C_REDIRECT_URI;
const homePage = window.location.origin;

export const b2cPolicies = {
	names: {
		signUpSignIn: adb2cSignUpSignIn,
		signUp: adb2cSignUp,
		forgotPassword: adb2cPasswordReset,
		changePassword: adb2cPasswordChange
	},
	authorities: {
		signUpSignIn: {
			authority: `https://${adb2cDomainName}.b2clogin.com/${adb2cDomainName}.onmicrosoft.com/${adb2cSignUpSignIn}`
		},
		signUp: {
			authority: `https://${adb2cDomainName}.b2clogin.com/${adb2cDomainName}.onmicrosoft.com/${adb2cSignUp}`,
			extraQueryParameters: {
				refCode: '123',
				logoLink: homePage
			}
		},
		forgotPassword: {
			authority: `https://${adb2cDomainName}.b2clogin.com/${adb2cDomainName}.onmicrosoft.com/${adb2cPasswordReset}`,
			extraQueryParameters: {
				ui_locales: localStorage?.getItem('userLanguage') ? localStorage?.getItem('userLanguage').toLowerCase() : ''
			}
		},
		changePassword: {
			authority: `https://${adb2cDomainName}.b2clogin.com/${adb2cDomainName}.onmicrosoft.com/${adb2cPasswordChange}`,
			extraQueryParameters: {
				ui_locales: localStorage?.getItem('userLanguage') ? localStorage?.getItem('userLanguage').toLowerCase() : ''
			}
		}
	},
	authorityDomain: `${adb2cDomainName}.b2clogin.com`
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
	auth: {
		clientId: adb2cClientID, // This is the ONLY mandatory field that you need to supply.
		authority: b2cPolicies.authorities.signUpSignIn.authority, // Use a sign-up/sign-in user-flow as a default authority
		knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
		redirectUri: adb2cRedirectUri, // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
		postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
		navigateToLoginRequestUrl: false // If "true", will navigate back to the original request location before processing the auth code response.
	},
	cache: {
		cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "sessionStorage" gives you SSO between tabs.
		storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
	},
	system: {
		iframeHashTimeout: 20000,
		loggerOptions: {
			loggerCallback: (level, message, containsPii) => {
				window.addEventListener(
					'message',
					e => {
						if (e.origin !== adb2cDomainName + '.b2clogin.com') {
							return;
						}
						e.source.postMessage(homePage, adb2cDomainName + '.b2clogin.com');
					},
					false
				);
				if (containsPii) {
					return;
				}
				switch (level) {
					case LogLevel.Error:
						//console.error(message);
						return;
					case LogLevel.Info:
						//console.info(message);
						return;
					case LogLevel.Verbose:
						//console.debug(message);
						return;
					case LogLevel.Warning:
						//console.warn(message);
						return;
				}
			}
		}
	}
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
	scopes: []
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
	scopes: ['openid', 'profile'],
	loginHint: 'example@domain.net'
};
